import { useEffect, useState, useMemo, FC } from 'react';
import SidePanel from '../../SidePanel';
import { formatDateTime } from '../../../lib/DateTime';
import FormAnswers from './FormAnswers';
import {
    ChevronLeftIcon,
    ArchiveBoxIcon,
} from '@heroicons/react/24/outline/index';
import PencilSquareIcon from '../../icons/PencilSquareIcon';
import DotDotDotMenu from '../../DotDotDotMenu';
import useDelete from '../../../lib/useDelete';
import EditFormSubmission from './EditFormSubmission';
import {
    useFormSubmissionDetailsQuery,
    useFormSubmissionSignatureQuery,
} from '../../../lib/Queries';
import SectionHeading from '../../SectionHeading';
import ClipboardText from '../../icons/ClipboardText';
import AuditLogs from '../../AuditLogs';
import useSaveFormSubmission from '../../../lib/useSaveFormSubmission';
import PendingUntilBanner from '../../calendar/PendingUntilBanner';
import Loading from '../../Loading';
import GrantList from '../../packages/GrantList';
import CustomTags from '../../customfields/CustomTags';
import { ObjectTypes } from '../../../lib/ObjectTypes';
import CustomFields from '~/components/customfields/CustomFields';
import SignatureHeader from '../../SignatureHeader';
import { QuestionType } from '~/lib/Questions/Questions.constants';
import { FormQuestion, FormSubmission } from '~/lib/Api.types';
import { onDownloadClick } from '~/components/SignatureHeader/SignatureHeader.utils';
import Payments from '~/components/scheduling/Payments';
import BookingEventList from '~/components/clients/BookingEventList';
import EventDetailsPanel from '~/components/calendar/EventDetailsPanel';
import GiftCertificatesList from '~/components/giftcertificates/GiftCertificatesList';
import CheckCircle2Icon from '~/components/icons/CheckCircle2Icon';

type SubmissionDetailsPanelProps = {
    submission: {
        submission: {
            id: FormSubmission['id'];
        } & Partial<Omit<FormSubmission, 'id'>>;
        form?: FormSubmission['form'];
    };
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
};
const SubmissionDetailsPanel: FC<SubmissionDetailsPanelProps> = ({
    submission,
    isOpen,
    onClose,
    refetch,
}) => {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const { saveDelete, isDeleting } = useDelete(
        `/submissions/${submission?.submission.id}/delete`,
    );
    const {
        data,
        isLoading,
        refetch: refetchDetails,
    } = useFormSubmissionDetailsQuery(submission?.submission?.id);

    // Make sure we always query freshest...
    submission = data || submission;
    const events = data?.events || [];
    const giftCertificates = data?.giftCertificates || [];

    const { data: submissionDetailsData } = useFormSubmissionSignatureQuery(
        submission?.submission?.id,
    );

    const [isEditing, setIsEditing] = useState(false);
    const { newAnswers, setNewAnswers, isSaving, doSave } =
        useSaveFormSubmission(submission?.submission?.id, () => {
            setIsEditing(false);
            refetch();
        });

    useEffect(() => {
        //setIsEditing(false)
    }, [submission?.submission?.id]);

    function doDelete() {
        console.log('Deleting...');
        saveDelete().then(() => {
            onClose();
            refetch();
        });
    }

    const answersIncludeSignature = useMemo(() => {
        return (
            submission?.submission?.answers?.some(
                ({ question }: { question: FormQuestion }) =>
                    question.type === QuestionType.Signature,
            ) ?? false
        );
    }, [submission?.submission?.answers]);

    const formSubmissionIsEditable =
        (submission?.submission?.answers?.length ?? 0) > 0 &&
        !answersIncludeSignature;

    const showSignatureLock = answersIncludeSignature;

    const dotDotDotOptions: Array<{
        name: string;
        isDelete?: boolean;
        onClick: () => void;
        icon: any;
    }> = [
        {
            name: 'Confirmation Page',
            onClick: () => {
                window
                    .open(submission?.submission?.confirmation_url, '_blank')
                    .focus();
            },
            icon: <CheckCircle2Icon />,
        },
        {
            name: 'Delete',
            isDelete: true,
            onClick: doDelete,
        },
    ];

    if (submissionDetailsData?.signedDocUrl) {
        dotDotDotOptions.push({
            name: 'Download form',
            onClick: () => {
                onDownloadClick(
                    submissionDetailsData?.signatureStatus,
                    submissionDetailsData?.signedDocUrl,
                );
            },
        });
    }

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            showSave={isEditing}
            saveLabel={isSaving ? 'Saving...' : 'Save'}
            onCancel={() => setIsEditing(false)}
            onSave={doSave}
            title={
                <div className="flex items-center">
                    <a
                        href="#"
                        className="no-underline pr-2"
                        onClick={ev => {
                            ev.preventDefault();
                            onClose();
                        }}
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                    </a>
                    Form Submission
                </div>
            }
        >
            <>
                {!!submission &&
                    Object.keys(submission).length > 0 &&
                    !!submission.submission?.created_at && (
                        <div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-lg font-semibold">
                                        {submission.form?.name}
                                    </div>
                                    <div className="text-gray-500 text-sm">
                                        Submitted{' '}
                                        {formatDateTime(
                                            submission.submission?.created_at,
                                        )}
                                    </div>
                                </div>

                                <CustomTags
                                    id={submission.submission?.client?.id}
                                    type={ObjectTypes.CLIENT}
                                    fields={
                                        submission.submission?.client
                                            ?.custom_fields
                                    }
                                />

                                <div className="flex items-center justify-end">
                                    {formSubmissionIsEditable && (
                                        <button
                                            type="button"
                                            className="btn-light-circle mr-2"
                                            onClick={() =>
                                                setIsEditing(!isEditing)
                                            }
                                        >
                                            <PencilSquareIcon className="w-4 h-4" />
                                        </button>
                                    )}
                                    {showSignatureLock &&
                                        submission?.submission?.id && (
                                            <SignatureHeader
                                                formSubmissionId={
                                                    submission.submission.id
                                                }
                                                hideDownloadButton
                                            />
                                        )}
                                    <DotDotDotMenu
                                        btnClassName="btn-light-circle"
                                        options={dotDotDotOptions}
                                    />
                                </div>
                            </div>

                            {!!submission.submission?.pending_until && (
                                <PendingUntilBanner
                                    className="mt-4"
                                    pendingUntil={
                                        submission.submission?.pending_until
                                    }
                                />
                            )}

                            {submission.submission.created_at !==
                                submission.submission.updated_at && (
                                <div className="text-gray-500">
                                    Last updated{' '}
                                    {formatDateTime(
                                        submission.submission.updated_at,
                                    )}
                                </div>
                            )}

                            {submission.submission.events_count === 0 && (
                                <CustomFields
                                    key={submission.submission?.id}
                                    type={ObjectTypes.FORM_SUBMISSION}
                                    id={submission.submission?.id}
                                    fields={
                                        submission.submission?.custom_fields
                                    }
                                    onChanged={() => null}
                                    className="mt-4"
                                />
                            )}

                            {isEditing && (
                                <EditFormSubmission
                                    currentQuestionsAndAnswers={
                                        submission.answers
                                    }
                                    newAnswers={newAnswers}
                                    setNewAnswers={setNewAnswers}
                                    key={submission?.submission?.id}
                                />
                            )}
                            {!isEditing && (
                                <>
                                    {isLoading &&
                                        !submission.answers?.length && (
                                            <Loading />
                                        )}

                                    {(!isLoading ||
                                        submission.answers?.length > 0) && (
                                        <FormAnswers
                                            answers={submission.answers}
                                        />
                                    )}
                                </>
                            )}

                            {!isEditing && (
                                <div>
                                    <GrantList
                                        search={{
                                            submissionId:
                                                submission.submission.id,
                                        }}
                                        hideWhenEmpty={true}
                                        headerContent={({ grants }) => (
                                            <>
                                                {grants.length > 0 && (
                                                    <SectionHeading
                                                        className="mt-4 mb-2"
                                                        name="Packages"
                                                        icon={
                                                            <ArchiveBoxIcon />
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                        rowContent={({ grant }) => (
                                            <div>{grant.package?.name}</div>
                                        )}
                                    />

                                    {events.length > 0 && (
                                        <BookingEventList
                                            className="mt-4"
                                            events={events}
                                            allEventsTogether={true}
                                            onClick={event => {
                                                setSelectedEvent(event);
                                            }}
                                        />
                                    )}

                                    {giftCertificates.length > 0 && (
                                        <GiftCertificatesList
                                            className="mt-4"
                                            refetch={refetchDetails}
                                            giftCertificates={giftCertificates}
                                        />
                                    )}

                                    <Payments
                                        id={submission.submission.id}
                                        type={ObjectTypes.FORM_SUBMISSION}
                                        headingClassName="mt-4"
                                    />

                                    <SectionHeading
                                        className="mt-4"
                                        name="Logs"
                                        icon={<ClipboardText />}
                                    />

                                    <AuditLogs
                                        id={submission.submission.id}
                                        type={ObjectTypes.FORM_SUBMISSION}
                                        className="mt-2"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                <EventDetailsPanel
                    isOpen={!!selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    event={selectedEvent}
                />
            </>
        </SidePanel>
    );
};

export default SubmissionDetailsPanel;
